import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, RotateCcwIcon } from 'lucide-react';
import { HookScreen } from './HookScreen';
import { ReframeScreen } from './ReframeScreen';
import { RevealScreen } from './RevealScreen';
import { ProofScreen } from './ProofScreen';
import { MiniSimScreen } from './MiniSimScreen';
import { PenScreen } from './PenScreen';
import { rekovelleStartingDose } from '../../utils/rekovelleDose';
type Answer = 'yes' | 'no' | null;
const SCREENS = 6;
const EASE = [0.23, 1, 0.32, 1] as const;
const DEFAULT_AMH = 14;
const DEFAULT_WEIGHT = 65;
export function TeaserSnak() {
  const reduced = useReducedMotion() ?? false;
  const [screen, setScreen] = useState(0);
  const [answer, setAnswer] = useState<Answer>(null);
  const [amh, setAmh] = useState(DEFAULT_AMH);
  const [weight, setWeight] = useState(DEFAULT_WEIGHT);
  const dose = rekovelleStartingDose(amh, weight);
  const next = () => setScreen(s => Math.min(SCREENS - 1, s + 1));
  const back = () => setScreen(s => Math.max(0, s - 1));
  const replay = () => {
    setAnswer(null);
    setAmh(DEFAULT_AMH);
    setWeight(DEFAULT_WEIGHT);
    setScreen(0);
  };
  return <section aria-label="REKOVELLE teaser Snak" className="flex h-full flex-1 flex-col bg-white px-6 pb-5 pt-4 font-sans text-ink" data-id="element-236" data-mp="lpobffw3din9">
      <div className="flex items-center gap-3" data-id="element-237" data-mp="cozzcqq9dsd0">
        <button type="button" onClick={back} disabled={screen === 0} aria-label="Previous screen" className="-ml-2 rounded-full p-1.5 text-graphite transition-[color,opacity] duration-150 hover:text-ink disabled:opacity-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent" data-id="element-238" data-mp="co82lckxdol5">
          <ChevronLeftIcon size={18} strokeWidth={2} data-id="element-239" data-mp="1x54num0p3og1" />
        </button>
        <div className="flex flex-1 gap-1.5" role="progressbar" aria-valuemin={1} aria-valuemax={SCREENS} aria-valuenow={screen + 1} data-id="element-240" data-mp="224h8r180alfz">
          {Array.from({
          length: SCREENS
        }).map((_, i) => <span key={i} className="h-[3px] flex-1 overflow-hidden rounded-full bg-ink/10" data-id="element-241" data-mp="20kxtz02mmojf" data-mpi={i}>
              <motion.span className="block h-full origin-left bg-ink" initial={false} animate={{
            scaleX: i <= screen ? 1 : 0
          }} transition={{
            duration: 0.25,
            ease: EASE
          }} data-id="element-242" data-mp="1vqk1p6j9gxiz" />
            </span>)}
        </div>
        <span className="w-7 text-right font-mono text-[10px] tracking-[0.1em] text-graphite" data-id="element-243" data-mp="2jfmikc62mmcf">{screen + 1}/{SCREENS}</span>
        <button type="button" onClick={replay} disabled={screen === 0} aria-label="Replay from the start" title="Replay" className="-mr-2 rounded-full p-1.5 text-graphite transition-[color,opacity] duration-150 hover:text-ink disabled:opacity-30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent" data-id="element-244" data-mp="29nql93bt7099">
          <RotateCcwIcon size={16} strokeWidth={2} data-id="element-245" data-mp="jux0244pyqij" />
        </button>
      </div>

      <div className="relative mt-5 flex flex-1 flex-col" data-id="element-246" data-mp="jx9nuxx5rv3i">
        <AnimatePresence mode="wait" initial={false} data-id="element-247" data-mp="out0jte7jztc">
          <motion.div key={screen} className="flex flex-1 flex-col [&>*]:flex-1" initial={{
          opacity: 0,
          x: reduced ? 0 : 16
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: reduced ? 0 : -16
        }} transition={{
          duration: 0.2,
          ease: EASE
        }} data-id="element-248" data-mp="svlegzy0ysvy">
            {screen === 0 && <HookScreen onAnswer={a => {
            setAnswer(a);
            next();
          }} data-id="element-249" data-mp="17r1avbm2v0vi" />}
            {screen === 1 && <ReframeScreen answer={answer} onNext={next} data-id="element-250" data-mp="1gjw8pk97tub9" />}
            {screen === 2 && <MiniSimScreen amh={amh} weight={weight} onAmh={setAmh} onWeight={setWeight} onNext={next} data-id="element-251" data-mp="1ll21fi2vlhks" />}
            {screen === 3 && <RevealScreen amh={amh} weight={weight} dose={dose} onNext={next} data-id="element-252" data-mp="1qk544vvdvbpl" />}
            {screen === 4 && <PenScreen dose={dose} onNext={next} data-id="element-253" data-mp="1cvbs4wy5of1e" />}
            {screen === 5 && <ProofScreen onReplay={replay} data-id="element-254" data-mp="17y380qtg60xi" />}
          </motion.div>
        </AnimatePresence>
      </div>

      <p className="mt-4 text-center text-[9.5px] leading-snug text-graphite/70" data-id="element-255" data-mp="1mmpqh1edf8sy">
        Conceptual prototype for discussion. Clinical content and claims subject to Ferring medical/legal review.
      </p>
    </section>;
}